import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Logo } from "@/components/Logo";
import { toast } from "sonner";
import { Building2, ArrowRight, CheckCircle2 } from "lucide-react";

export default function AcceptInvite() {
  const { token } = useParams();
  const navigate = useNavigate();
  const { user, acceptInvite } = useAuth();
  const [invite, setInvite] = useState(null);
  const [error, setError] = useState(null);
  const [accepting, setAccepting] = useState(false);

  useEffect(() => {
    api.get(`/invitations/${token}`)
      .then((r) => setInvite(r.data))
      .catch((e) => setError(e.response?.data?.detail || "Invitation not found"));
  }, [token]);

  const accept = async () => {
    if (!user) {
      navigate(`/signup?invite=${token}&email=${encodeURIComponent(invite.email)}`);
      return;
    }
    if (user.email.toLowerCase() !== invite.email.toLowerCase()) {
      toast.error(`This invite is for ${invite.email}. Log out and sign in as that user.`);
      return;
    }
    setAccepting(true);
    try {
      await acceptInvite(token);
      toast.success(`Joined ${invite.org_name}!`);
      navigate("/app");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not accept invitation");
    } finally {
      setAccepting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0B0F17] flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-grid bg-grid-fade opacity-30 pointer-events-none" />
      <Card className="relative w-full max-w-md bg-slate-900/90 border-slate-800 p-8 rounded-2xl">
        <div className="mb-6 flex justify-center"><Logo size="lg" /></div>
        {error && (
          <>
            <h1 className="font-display text-xl font-bold mb-2 text-rose-400">Invalid invitation</h1>
            <p className="text-sm text-slate-400 mb-6">{error}</p>
            <Link to="/" className="text-orange-400 text-sm hover:text-orange-300">← Back to home</Link>
          </>
        )}
        {!error && !invite && <p className="text-slate-400">Loading…</p>}
        {invite && (
          <>
            <div className="flex items-center gap-3 mb-6 p-4 rounded-lg bg-slate-800/60 border border-slate-800">
              <div className="w-11 h-11 rounded-lg bg-orange-500/20 border border-orange-500/30 flex items-center justify-center">
                <Building2 className="w-5 h-5 text-orange-400" />
              </div>
              <div>
                <p className="text-xs text-slate-500 font-mono uppercase tracking-widest">You've been invited to</p>
                <p className="font-display text-lg font-bold" data-testid="invite-org-name">{invite.org_name}</p>
              </div>
            </div>
            <h1 className="font-display text-2xl font-bold mb-2">Join {invite.org_name}</h1>
            <p className="text-sm text-slate-400 mb-4">
              <span className="text-slate-300">{invite.invited_by_email}</span> invited{" "}
              <span className="text-white font-semibold">{invite.email}</span> to join as{" "}
              <Badge className="ml-1 bg-orange-500/15 text-orange-300 border-orange-500/30 capitalize" data-testid="invite-role-badge">{invite.role}</Badge>
            </p>
            {user && user.email.toLowerCase() !== invite.email.toLowerCase() && (
              <p className="text-xs text-amber-400 bg-amber-500/10 border border-amber-500/30 rounded-lg p-3 mb-4">
                You're signed in as {user.email}. This invitation is for {invite.email}. Please log out first.
              </p>
            )}
            <Button onClick={accept} disabled={accepting} data-testid="invite-accept" className="w-full bg-orange-500 hover:bg-orange-600 text-slate-900 font-bold h-11">
              {accepting ? "Joining…" : user ? (
                <>
                  <CheckCircle2 className="w-4 h-4 mr-1" /> Accept & join
                </>
              ) : (
                <>Create account & join <ArrowRight className="w-4 h-4 ml-1" /></>
              )}
            </Button>
            {!user && (
              <p className="text-xs text-slate-500 mt-4 text-center">
                Already have an account? <Link to={`/login?next=/invite/${token}`} className="text-orange-400 hover:text-orange-300">Log in</Link>
              </p>
            )}
          </>
        )}
      </Card>
    </div>
  );
}
